import {
  Box,
  Button,
  Input,
  VStack,
  Heading,
  useToast,
  Text,
  SimpleGrid,
  Flex,
  Spinner,
  IconButton,
  Divider,
  Textarea,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useJsApiLoader } from "@react-google-maps/api";
import { useAuth } from "../../auth/AuthContext";
import { RootState } from "../../store/store";
import AutocompleteInput from "../../LocationsModule/PlaceAutocompleteInput";
import MapDisplay from "../../LocationsModule/MapDisplay";
import {
  Package,
  ShoppingBag,
  Truck,
  BoxIcon,
  Plus,
  Minus,
  X,
  MapPin,
  Warehouse,
  ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import AppPhoneInput, {
  AppPhoneCountryCode,
  buildInternationalNumber,
} from "../../components/Shared/PhoneInput";
import GlobalSpinner from "../../components/CustomReusables/GlobalSpinner";

type ParcelSize = "small" | "medium" | "large" | "extra";

type DeliveryItem = {
  name: string;
  qty: number;
  size: ParcelSize;
  nameTouched?: boolean;
};

type DeliveryLocationMode = "address" | "warehouse";

type PickupPoint = {
  id: string;
  name: string;
  address: string;
  city?: string;
  lat: number;
  lng: number;
  isDefault?: boolean;
};

const cardsBorderRadii = "8px";

const LOCATION_MODE_OPTIONS = [
  {
    value: "address" as DeliveryLocationMode,
    label: "Enter address",
    description: "Enter a pickup or drop-off address",
    icon: MapPin,
  },
  {
    value: "warehouse" as DeliveryLocationMode,
    label: "PickDengu point",
    description: "Use an available PickDengu point",
    icon: Warehouse,
  },
];

const normalizePickupPoints = (payload: any): PickupPoint[] => {
  const rawPoints = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.data?.data)
      ? payload.data.data
      : Array.isArray(payload?.pickupPoints)
        ? payload.pickupPoints
        : Array.isArray(payload)
          ? payload
          : [];

  return rawPoints
    .map((point: any) => {
      const id = String(point?.id || point?._id || "").trim();
      const name = String(
        point?.name || point?.label || "PickDengu point",
      ).trim();
      const address = String(point?.address || "").trim();
      const city = String(point?.city || "").trim();
      const lat = Number(point?.lat ?? point?.latitude);
      const lng = Number(point?.lng ?? point?.longitude);

      return {
        id,
        name,
        address,
        city,
        lat,
        lng,
        isDefault: Boolean(point?.isDefault),
      };
    })
    .filter(
      (point: PickupPoint) =>
        Boolean(point.id) &&
        Boolean(point.address) &&
        Number.isFinite(point.lat) &&
        Number.isFinite(point.lng),
    )
    .sort((a: PickupPoint, b: PickupPoint) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
};

const pricePerKm = {
  small: 400,
  medium: 600,
  large: 900,
  extra: 1300,
};

const parcelOptions = [
  {
    key: "small",
    label: "Small",
    icon: ShoppingBag,
    desc: "Documents, letters, papers",
    dimension: "Up to 30x30 cm / < 2kg",
  },
  {
    key: "medium",
    label: "Medium",
    icon: BoxIcon,
    desc: "Shoes, small gadgets, clothes",
    dimension: "Up to 60x60 cm / < 10kg",
  },
  {
    key: "large",
    label: "Large",
    icon: Package,
    desc: "Laptops, boxes, household items",
    dimension: "Up to 1m / < 25kg",
  },
  {
    key: "extra",
    label: "Extra Large",
    icon: Truck,
    desc: "Furniture, large packages",
    dimension: "Up to 2m / < 60kg",
  },
];

const fallbackCenter = { lat: -13.9626, lng: 33.7741 };

const getItemBaseSurcharge = (size: ParcelSize) => {
  switch (size) {
    case "small":
      return 0;
    case "medium":
      return 1200;
    case "large":
      return 2500;
    case "extra":
      return 4500;
    default:
      return 0;
  }
};

const isValidEmail = (value: string) => {
  if (!value.trim()) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

// Exact phone validation used by Login.tsx. Keeping it here means delivery
// remains protected even if the running app still has an older PhoneInput.
const getPhoneValidationErrorLikeLogin = (
  countryCode: AppPhoneCountryCode,
  phoneNumber: string,
) => {
  const digits = String(phoneNumber || "").replace(/\D/g, "");
  const localDigits = digits.replace(/^0+/, "");

  if (!digits) {
    return "Phone number is required.";
  }

  if (countryCode === "ZA") {
    if (localDigits.length !== 9 || !/^[6-8]\d{8}$/.test(localDigits)) {
      return "Enter a valid South African phone number.";
    }
  }

  if (countryCode === "MW") {
    if (
      localDigits.length < 8 ||
      localDigits.length > 9 ||
      !/^[189]\d{7,8}$/.test(localDigits)
    ) {
      return "Enter a valid Malawi phone number.";
    }
  }

  return "";
};

const limitPhoneNumberLikeLogin = (value: string) => {
  const digits = String(value || "").replace(/\D/g, "");
  const maxLength = digits.startsWith("0") ? 10 : 9;
  return digits.slice(0, maxLength);
};

const getSizeLabel = (size: ParcelSize) =>
  parcelOptions.find((option) => option.key === size)?.label || "Small";

export const RequestDelivery = () => {
  const [name, setName] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] =
    useState<AppPhoneCountryCode>("MW");
  const [phone, setPhone] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [email, setEmail] = useState("");
  const [deliveryInstructions, setDeliveryInstructions] = useState("");

  // Same location modes as the mobile delivery flow. Own address stays the default.
  const [pickupMode, setPickupMode] = useState<DeliveryLocationMode>("address");
  const [dropoffMode, setDropoffMode] =
    useState<DeliveryLocationMode>("address");
  const [pickupPoints, setPickupPoints] = useState<PickupPoint[]>([]);
  const [pickupPointsLoading, setPickupPointsLoading] = useState(false);
  const [pickupPointsError, setPickupPointsError] = useState("");
  const [selectedPickupPointId, setSelectedPickupPointId] = useState("");
  const [selectedDropoffPointId, setSelectedDropoffPointId] = useState("");

  const [fromAddress, setFromAddress] = useState("");
  const [fromCoords, setFromCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [toAddress, setToAddress] = useState("");
  const [toCoords, setToCoords] = useState<{ lat: number; lng: number } | null>(
    null,
  );

  // Preserve manually selected addresses when switching between Own address
  // and PickDengu point, matching the mobile delivery behaviour.
  const [savedPickupAddress, setSavedPickupAddress] = useState("");
  const [savedPickupCoords, setSavedPickupCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [savedDropoffAddress, setSavedDropoffAddress] = useState("");
  const [savedDropoffCoords, setSavedDropoffCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const [items, setItems] = useState<DeliveryItem[]>([
    { name: "", qty: 1, size: "small" },
  ]);
  const [validateItemNames, setValidateItemNames] = useState(false);
  const itemNameRefs = useRef<(HTMLInputElement | null)[]>([]);
  const itemNameErrors = items.map((item) =>
    Boolean((item.nameTouched || validateItemNames) && !item.name.trim()),
  );

  const [directions, setDirections] =
    useState<google.maps.DirectionsResult | null>(null);
  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");
  const [distanceKm, setDistanceKm] = useState(0);
  const [cost, setCost] = useState<number | null>(null);

  const toast = useToast();
  const { user } = useAuth();
  const { loading } = useSelector((state: RootState) => state.deliveryRequests);
  const navigate = useNavigate();
  const { theme } = useCustomTheme();

  const isDark = theme.name === "dark";
  const brand = theme.brand;

  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const textColor = theme.text;
  const mutedText = theme.textMuted;
  const subtleText = theme.textSubtle;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;

  const inputBg = theme.inputBg;
  const inputBorder = theme.inputBorder;
  const focusBorder = theme.inputBorderFocus;

  const panelShadow = theme.shadowMd;
  const cardShadow = theme.shadowSm;

  const primaryBtnBg = theme.gradients?.primaryButton || theme.primary;
  const primaryBtnHover =
    theme.gradients?.primaryButtonHover || theme.primaryHover;

  const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries: ["places"],
  });

  const phoneValidationError = useMemo(
    () => getPhoneValidationErrorLikeLogin(phoneCountryCode, phone),
    [phoneCountryCode, phone],
  );

  const fullPhoneNumber = useMemo(
    () =>
      phoneValidationError
        ? ""
        : buildInternationalNumber(phoneCountryCode, phone),
    [phoneCountryCode, phone, phoneValidationError],
  );

  const deliveryDetailsComplete = Boolean(
    name.trim() && !phoneValidationError && isValidEmail(email) &&
    fromAddress && fromCoords && toAddress && toCoords,
  );

  useEffect(() => {
    if (deliveryDetailsComplete) setValidateItemNames(true);
  }, [deliveryDetailsComplete]);

  const selectedPickupPoint = useMemo(
    () =>
      pickupPoints.find((point) => point.id === selectedPickupPointId) || null,
    [pickupPoints, selectedPickupPointId],
  );

  const selectedDropoffPoint = useMemo(
    () =>
      pickupPoints.find((point) => point.id === selectedDropoffPointId) || null,
    [pickupPoints, selectedDropoffPointId],
  );

  const loadPickupPoints = useCallback(async () => {
    try {
      setPickupPointsLoading(true);
      setPickupPointsError("");

      const apiBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
        /\/+$/,
        "",
      );

      // This endpoint is intentionally public/read-only, matching mobile.
      // Do not attach an auth token here; a stale/invalid token must never
      // prevent the delivery form from loading PickDengu points.
      const response = await axios.get(
        `${apiBase}/api/v1/deliveries/pickup-points`,
      );

      setPickupPoints(normalizePickupPoints(response.data));
    } catch (error) {
      console.error("Failed to load PickDengu pickup points:", error);
      setPickupPoints([]);
      setPickupPointsError(
        "PickDengu points could not be loaded. Please retry or use your own address.",
      );
    } finally {
      setPickupPointsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPickupPoints();
  }, [loadPickupPoints]);

  const resetRoute = useCallback(() => {
    setDirections(null);
    setDistance("");
    setDuration("");
    setDistanceKm(0);
    setCost(null);
  }, []);

  const selectPickupMode = (mode: DeliveryLocationMode) => {
    if (pickupMode === mode) return;

    if (mode === "warehouse") {
      setSavedPickupAddress(fromAddress);
      setSavedPickupCoords(fromCoords);
      setFromAddress("");
      setFromCoords(null);
      setSelectedPickupPointId("");
    } else {
      setFromAddress(savedPickupAddress);
      setFromCoords(savedPickupCoords);
      setSelectedPickupPointId("");
    }

    setPickupMode(mode);
    resetRoute();
  };

  const selectDropoffMode = (mode: DeliveryLocationMode) => {
    if (dropoffMode === mode) return;

    if (mode === "warehouse") {
      setSavedDropoffAddress(toAddress);
      setSavedDropoffCoords(toCoords);
      setToAddress("");
      setToCoords(null);
      setSelectedDropoffPointId("");
    } else {
      setToAddress(savedDropoffAddress);
      setToCoords(savedDropoffCoords);
      setSelectedDropoffPointId("");
    }

    setDropoffMode(mode);
    resetRoute();
  };

  const selectPickupPoint = (point: PickupPoint) => {
    setSelectedPickupPointId(point.id);
    setFromAddress(point.address);
    setFromCoords({ lat: point.lat, lng: point.lng });
    resetRoute();
  };

  const selectDropoffPoint = (point: PickupPoint) => {
    setSelectedDropoffPointId(point.id);
    setToAddress(point.address);
    setToCoords({ lat: point.lat, lng: point.lng });
    resetRoute();
  };

  const sanitizedItems = useMemo(
    () =>
      items
        .map((item) => ({
          name: item.name.trim(),
          qty: Number(item.qty) || 1,
          size: item.size,
        }))
        .filter((item) => item.name && item.qty > 0),
    [items],
  );

  const totalItems = useMemo(
    () => sanitizedItems.reduce((sum, item) => sum + item.qty, 0),
    [sanitizedItems],
  );

  const itemSizeBreakdown = useMemo(() => {
    return sanitizedItems.reduce(
      (acc, item) => {
        const distanceFeeForOne = pricePerKm[item.size] * distanceKm;
        const baseFeeForOne = getItemBaseSurcharge(item.size);
        const itemTotal = (distanceFeeForOne + baseFeeForOne) * item.qty;

        return {
          totalDistanceFee: acc.totalDistanceFee + distanceFeeForOne * item.qty,
          totalBaseSurcharge: acc.totalBaseSurcharge + baseFeeForOne * item.qty,
          totalEstimatedFee: acc.totalEstimatedFee + itemTotal,
        };
      },
      {
        totalDistanceFee: 0,
        totalBaseSurcharge: 0,
        totalEstimatedFee: 0,
      },
    );
  }, [sanitizedItems, distanceKm]);

  const updateItemName = (index: number, value: string) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, name: value } : item)),
    );
  };

  const updateItemSize = (index: number, size: ParcelSize) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, size, nameTouched: true } : item)),
    );
  };

  const increaseQty = (index: number) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, qty: Math.max(1, item.qty + 1) } : item,
      ),
    );
  };

  const decreaseQty = (index: number) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, qty: Math.max(1, item.qty - 1) } : item,
      ),
    );
  };

  const addItem = () => {
    setItems((prev) => [...prev, { name: "", qty: 1, size: "small" }]);
  };

  const removeItem = (index: number) => {
    setItems((prev) => {
      if (prev.length === 1) {
        return [{ name: "", qty: 1, size: "small" }];
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const fetchDirections = useCallback(() => {
    if (!fromCoords || !toCoords || !window.google) return;

    const directionsService = new google.maps.DirectionsService();

    directionsService.route(
      {
        origin: fromCoords,
        destination: toCoords,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK" && result) {
          setDirections(result);

          const leg = result.routes[0]?.legs[0];
          const legDistanceText = leg?.distance?.text || "";
          const legDurationText = leg?.duration?.text || "";

          setDistance(legDistanceText);
          setDuration(legDurationText);

          const parsedKm = parseFloat(
            legDistanceText.replace(/[^0-9.]/g, "") || "0",
          );

          setDistanceKm(parsedKm);

          const estimatedCost = itemSizeBreakdown.totalEstimatedFee;
          setCost(Number(estimatedCost.toFixed(2)));
        } else {
          console.error("Directions request failed:", status);
          setDirections(null);
          setDistance("");
          setDuration("");
          setDistanceKm(0);
          setCost(null);
        }
      },
    );
  }, [fromCoords, toCoords, itemSizeBreakdown.totalEstimatedFee]);

  useEffect(() => {
    if (fromCoords && toCoords) {
      fetchDirections();
    }
  }, [fromCoords, toCoords, fetchDirections]);

  useEffect(() => {
    if (fromCoords && toCoords && directions) {
      const estimatedCost = itemSizeBreakdown.totalEstimatedFee;
      setCost(Number(estimatedCost.toFixed(2)));
    }
  }, [
    items,
    fromCoords,
    toCoords,
    directions,
    itemSizeBreakdown.totalEstimatedFee,
  ]);

  const handleContinue = () => {
    setValidateItemNames(true);
    if (!name.trim()) {
      toast({
        status: "error",
        title: "Full name is required.",
      });
      return;
    }

    setPhoneTouched(true);

    if (phoneValidationError) {
      toast({
        status: "error",
        title: phoneValidationError,
      });
      return;
    }

    if (!isValidEmail(email)) {
      toast({
        status: "error",
        title: "Please enter a valid email address.",
      });
      return;
    }

    if (pickupMode === "warehouse" && !selectedPickupPoint) {
      toast({
        status: "error",
        title: "Please choose a PickDengu pickup point.",
      });
      return;
    }

    if (dropoffMode === "warehouse" && !selectedDropoffPoint) {
      toast({
        status: "error",
        title: "Please choose a PickDengu drop-off point.",
      });
      return;
    }

    if (!fromAddress || !fromCoords) {
      toast({
        status: "error",
        title: "Please select a valid pickup location.",
      });
      return;
    }

    if (!toAddress || !toCoords) {
      toast({
        status: "error",
        title: "Please select a valid drop-off location.",
      });
      return;
    }

    const normalizedFromAddress = fromAddress.trim().toLowerCase();
    const normalizedToAddress = toAddress.trim().toLowerCase();
    const sameCoordinates =
      Math.abs(fromCoords.lat - toCoords.lat) < 0.000001 &&
      Math.abs(fromCoords.lng - toCoords.lng) < 0.000001;

    if (normalizedFromAddress === normalizedToAddress || sameCoordinates) {
      toast({
        status: "error",
        title: "Pickup and drop-off locations cannot be the same.",
      });
      return;
    }

    const missingItemIndex = items.findIndex((item) => !item.name.trim());
    if (missingItemIndex !== -1) {
      itemNameRefs.current[missingItemIndex]?.focus();
      toast({
        status: "error",
        title: `Please enter a name for item ${missingItemIndex + 1}.`,
      });
      return;
    }

    if (cost === null || !distance || !duration) {
      toast({
        status: "error",
        title: "Please calculate the route before continuing.",
      });
      return;
    }

    navigate("/delivery-checkout", {
      state: {
        senderId: user?.uid || "",
        name: name.trim(),
        phone: fullPhoneNumber,
        email: email.trim(),
        deliveryInstructions: deliveryInstructions.trim(),

        pickup: {
          address: fromAddress,
          lat: fromCoords.lat,
          lng: fromCoords.lng,
        },
        dropoff: {
          address: toAddress,
          lat: toCoords.lat,
          lng: toCoords.lng,
        },

        // Preserve the mobile pickup/drop-off point semantics for checkout.
        pickupMode,
        dropoffMode,
        pickupWarehouseId: selectedPickupPoint?.id || "",
        pickupWarehouseName: selectedPickupPoint?.name || "",
        pickupWarehouseAddress: selectedPickupPoint?.address || "",
        dropoffWarehouseId: selectedDropoffPoint?.id || "",
        dropoffWarehouseName: selectedDropoffPoint?.name || "",
        dropoffWarehouseAddress: selectedDropoffPoint?.address || "",
        dispatchWarehouseId: selectedPickupPoint?.id || "",
        platformWarehouseId: selectedPickupPoint?.id || "",
        sellerBusinessName: selectedPickupPoint?.name || "Direct Delivery",

        // frontend rich items
        detailedItems: sanitizedItems,

        // backend safe items
        items: sanitizedItems.map((item) => ({
          name: `${item.name} (${getSizeLabel(item.size)})`,
          qty: item.qty,
        })),

        fee: cost,

        routeMeta: {
          distance,
          duration,
          distanceKm,
          totalItems,
          totalDistanceFee: itemSizeBreakdown.totalDistanceFee,
          totalBaseSurcharge: itemSizeBreakdown.totalBaseSurcharge,
        },

        fromAddress,
        fromCoords,
        toAddress,
        toCoords,
        cost,
        distance,
        duration,
      },
    });
  };

  if (!isLoaded) {
    return (
      <Flex justify="center" align="center" minH="220px" w="100%">
        <GlobalSpinner />
      </Flex>
    );
  }

  return (
    <Box
      mx="auto"
      bg={cardBg}
      borderRadius={cardsBorderRadii}
      p={{ base: 4, md: 5 }}
      boxShadow={panelShadow}
      color={textColor}
    >
      <Heading size="md" mb={4} color={textColor}>
        Request Delivery
      </Heading>

      <VStack spacing={5} align="stretch">
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Input
            placeholder="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            bg={inputBg}
            color={textColor}
            border={`1px solid ${inputBorder}`}
            _placeholder={{ color: theme.inputPlaceholder }}
            _hover={{ borderColor: borderStrong }}
            _focus={{
              borderColor: focusBorder,
              boxShadow: `0 0 0 1px ${focusBorder}`,
            }}
          />
          <FormControl
            isRequired
            isInvalid={phoneTouched && Boolean(phoneValidationError)}
          >
            <AppPhoneInput
              value={phone}
              countryCode={phoneCountryCode}
              placeholder="Phone number"
              onCountryChange={(value) => {
                setPhoneCountryCode(value);
                setPhone("");
                setPhoneTouched(true);
              }}
              onNumberChange={(value) => {
                setPhone(limitPhoneNumberLikeLogin(value));
                setPhoneTouched(true);
              }}
              onBlur={() => setPhoneTouched(true)}
              theme={theme}
              isRequired
              isInvalid={phoneTouched && Boolean(phoneValidationError)}
            />
            <FormErrorMessage>{phoneValidationError}</FormErrorMessage>
          </FormControl>
          <Input
            placeholder="Email (optional)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            bg={inputBg}
            color={textColor}
            border={`1px solid ${inputBorder}`}
            _placeholder={{ color: theme.inputPlaceholder }}
            _hover={{ borderColor: borderStrong }}
            _focus={{
              borderColor: focusBorder,
              boxShadow: `0 0 0 1px ${focusBorder}`,
            }}
          />
        </SimpleGrid>

        <Box>
          <Flex
            justify="space-between"
            align={{ base: "flex-start", sm: "center" }}
            direction={{ base: "column", sm: "row" }}
            gap={3}
            mb={3}
          >
            <Box>
              <Text fontSize="sm" fontWeight="700" color={textColor}>
                Pickup
              </Text>
              <Text fontSize="xs" color={mutedText}>
                Use your own address or hand the parcel in at a PickDengu point.
              </Text>
            </Box>

            <SimpleGrid
              columns={{ base: 1, sm: 2 }}
              spacing={2}
              w={{ base: "100%", sm: "auto" }}
            >
              {LOCATION_MODE_OPTIONS.map((option) => {
                const selected = pickupMode === option.value;
                const IconComp = option.icon;

                return (
                  <Box
                    as="label"
                    key={`pickup-mode-${option.value}`}
                    cursor="pointer"
                    minW={{ base: "100%", sm: "190px" }}
                    minH="40px"
                    px={3}
                    py={1.5}
                    borderRadius={cardsBorderRadii}
                    border={`2px solid ${
                      selected ? theme.primary : borderColor
                    }`}
                    bg={
                      selected
                        ? theme.selected ||
                          (isDark ? "rgba(49,130,206,0.12)" : "blue.50")
                        : inputBg
                    }
                    transition="all 0.2s ease"
                    _hover={{
                      borderColor: theme.primary,
                      boxShadow: cardShadow,
                    }}
                  >
                    <Flex align="center" gap={2.5}>
                      <input
                        type="radio"
                        name="pickup-location-mode"
                        value={option.value}
                        checked={selected}
                        onChange={() => selectPickupMode(option.value)}
                        style={{
                          width: "16px",
                          height: "16px",
                          margin: 0,
                          flexShrink: 0,
                          cursor: "pointer",
                          accentColor: theme.primary || "#3182CE",
                        }}
                      />
                      <IconComp
                        size={15}
                        color={selected ? theme.primary : mutedText}
                      />
                      <Box minW={0}>
                        <Text
                          fontSize="13px"
                          lineHeight="16px"
                          fontWeight="700"
                          color={selected ? theme.primary : textColor}
                        >
                          {option.label}
                        </Text>
                        <Text
                          fontSize="11px"
                          lineHeight="14px"
                          color={mutedText}
                        >
                          {option.description}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                );
              })}
            </SimpleGrid>
          </Flex>

          {pickupMode === "address" ? (
            <AutocompleteInput
              placeholder="Pickup location"
              value={fromAddress}
              onInputChange={(value) => {
                // User is editing again. Keep the text, but invalidate the old
                // Google coordinates until a new suggestion is selected.
                setFromAddress(value);
                setSavedPickupAddress(value);
                setFromCoords(null);
                setSavedPickupCoords(null);
                resetRoute();
              }}
              onPlaceSelected={(address) => {
                // This is a confirmed Google place, not just the search text.
                setFromAddress(address);
                setSavedPickupAddress(address);
                resetRoute();
              }}
              onLocationSelected={(coords) => {
                setFromCoords(coords);
                setSavedPickupCoords(coords);
                resetRoute();
              }}
            />
          ) : (
            <Box
              border={`1px solid ${borderColor}`}
              borderRadius={cardsBorderRadii}
              p={2}
              bg={softBg}
            >
              {pickupPointsLoading ? (
                <Flex align="center" gap={2} py={2}>
                   <GlobalSpinner />
                  <Text fontSize="sm" color={mutedText}>
                    Loading PickDengu points...
                  </Text>
                </Flex>
              ) : pickupPointsError ? (
                <Flex
                  align={{ base: "stretch", sm: "center" }}
                  justify="space-between"
                  direction={{ base: "column", sm: "row" }}
                  gap={3}
                >
                  <Text fontSize="sm" color={mutedText}>
                    {pickupPointsError}
                  </Text>
                  <Button size="sm" onClick={() => void loadPickupPoints()}>
                    Retry
                  </Button>
                </Flex>
              ) : pickupPoints.length === 0 ? (
                <Text fontSize="sm" color={mutedText}>
                  No PickDengu points are currently available.
                </Text>
              ) : (
                <Menu placement="bottom-start" matchWidth>
                  <MenuButton
                    as={Button}
                    w="100%"
                    h="40px"
                    minH="40px"
                    px={3}
                    py={0}
                    bg={inputBg}
                    color={textColor}
                    border={`1px solid ${
                      selectedPickupPoint ? theme.primary : inputBorder
                    }`}
                    borderRadius={cardsBorderRadii}
                    textAlign="left"
                    fontWeight="500"
                    rightIcon={<ChevronDown size={15} />}
                    _hover={{
                      borderColor: theme.primary,
                      bg: softBg,
                    }}
                    _active={{
                      borderColor: theme.primary,
                      bg: softBg,
                    }}
                  >
                    <Flex align="center" gap={2.5} minW={0}>
                      <Warehouse
                        size={15}
                        color={selectedPickupPoint ? theme.primary : mutedText}
                      />
                      <Box minW={0} flex="1">
                        <Text
                          fontSize="sm"
                          fontWeight={selectedPickupPoint ? "700" : "500"}
                          color={selectedPickupPoint ? textColor : mutedText}
                          noOfLines={1}
                        >
                          {selectedPickupPoint?.name ||
                            "Select PickDengu pickup point"}
                          {selectedPickupPoint?.isDefault ? " · Main" : ""}
                        </Text>
                        {selectedPickupPoint && (
                          <Text
                            fontSize="10px"
                            lineHeight="12px"
                            color={mutedText}
                            noOfLines={1}
                          >
                            {[
                              selectedPickupPoint.city,
                              selectedPickupPoint.address,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </Text>
                        )}
                      </Box>
                    </Flex>
                  </MenuButton>

                  <MenuList
                    bg={inputBg}
                    color={textColor}
                    borderColor={borderColor}
                    borderRadius={cardsBorderRadii}
                    boxShadow="lg"
                    py={0.5}
                    maxH="280px"
                    overflowY="auto"
                    zIndex={30}
                    minW="100%"
                  >
                    {pickupPoints.map((point) => {
                      const selected = selectedPickupPointId === point.id;

                      return (
                        <MenuItem
                          key={`pickup-${point.id}`}
                          onClick={() => selectPickupPoint(point)}
                          bg={selected ? softBg : inputBg}
                          _hover={{ bg: softBg }}
                          _focus={{ bg: softBg }}
                          h="40px"
                          minH="40px"
                          maxH="40px"
                          px={3}
                          py={0}
                          overflow="hidden"
                        >
                          <Flex align="center" gap={2.5} w="100%" h="100%">
                            <Warehouse
                              size={14}
                              color={selected ? theme.primary : mutedText}
                            />
                            <Box minW={0} flex="1">
                              <Flex align="center" gap={2}>
                                <Text
                                  fontSize="12px"
                                  lineHeight="13px"
                                  fontWeight={selected ? "800" : "700"}
                                  color={selected ? theme.primary : textColor}
                                  noOfLines={1}
                                >
                                  {point.name}
                                </Text>
                                {point.isDefault && (
                                  <Text
                                    fontSize="9px"
                                    lineHeight="10px"
                                    fontWeight="800"
                                    color={theme.primary}
                                    textTransform="uppercase"
                                  >
                                    Main
                                  </Text>
                                )}
                              </Flex>
                              <Text
                                fontSize="9px"
                                lineHeight="10px"
                                color={mutedText}
                                mt={0}
                                noOfLines={1}
                              >
                                {[point.city, point.address]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </Text>
                            </Box>
                          </Flex>
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Menu>
              )}
            </Box>
          )}
        </Box>

        <Box>
          <Flex
            justify="space-between"
            align={{ base: "flex-start", sm: "center" }}
            direction={{ base: "column", sm: "row" }}
            gap={3}
            mb={3}
          >
            <Box>
              <Text fontSize="sm" fontWeight="700" color={textColor}>
                Drop-off
              </Text>
              <Text fontSize="xs" color={mutedText}>
                Deliver to an address or let the recipient collect from a
                PickDengu point.
              </Text>
            </Box>

            <SimpleGrid
              columns={{ base: 1, sm: 2 }}
              spacing={2}
              w={{ base: "100%", sm: "auto" }}
            >
              {LOCATION_MODE_OPTIONS.map((option) => {
                const selected = dropoffMode === option.value;
                const IconComp = option.icon;

                return (
                  <Box
                    as="label"
                    key={`dropoff-mode-${option.value}`}
                    cursor="pointer"
                    minW={{ base: "100%", sm: "190px" }}
                    minH="40px"
                    px={3}
                    py={1.5}
                    borderRadius={cardsBorderRadii}
                    border={`2px solid ${
                      selected ? theme.primary : borderColor
                    }`}
                    bg={
                      selected
                        ? theme.selected ||
                          (isDark ? "rgba(49,130,206,0.12)" : "blue.50")
                        : inputBg
                    }
                    transition="all 0.2s ease"
                    _hover={{
                      borderColor: theme.primary,
                      boxShadow: cardShadow,
                    }}
                  >
                    <Flex align="center" gap={2.5}>
                      <input
                        type="radio"
                        name="dropoff-location-mode"
                        value={option.value}
                        checked={selected}
                        onChange={() => selectDropoffMode(option.value)}
                        style={{
                          width: "16px",
                          height: "16px",
                          margin: 0,
                          flexShrink: 0,
                          cursor: "pointer",
                          accentColor: theme.primary || "#3182CE",
                        }}
                      />
                      <IconComp
                        size={15}
                        color={selected ? theme.primary : mutedText}
                      />
                      <Box minW={0}>
                        <Text
                          fontSize="13px"
                          lineHeight="16px"
                          fontWeight="700"
                          color={selected ? theme.primary : textColor}
                        >
                          {option.label}
                        </Text>
                        <Text
                          fontSize="11px"
                          lineHeight="14px"
                          color={mutedText}
                        >
                          {option.description}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                );
              })}
            </SimpleGrid>
          </Flex>

          {dropoffMode === "address" ? (
            <AutocompleteInput
              placeholder="Drop-off location"
              value={toAddress}
              onInputChange={(value) => {
                // User is editing again. Keep the text, but invalidate the old
                // Google coordinates until a new suggestion is selected.
                setToAddress(value);
                setSavedDropoffAddress(value);
                setToCoords(null);
                setSavedDropoffCoords(null);
                resetRoute();
              }}
              onPlaceSelected={(address) => {
                // This is a confirmed Google place, not just the search text.
                setToAddress(address);
                setSavedDropoffAddress(address);
                resetRoute();
              }}
              onLocationSelected={(coords) => {
                setToCoords(coords);
                setSavedDropoffCoords(coords);
                resetRoute();
              }}
            />
          ) : (
            <Box
              border={`1px solid ${borderColor}`}
              borderRadius={cardsBorderRadii}
              p={2}
              bg={softBg}
            >
              {pickupPointsLoading ? (
                <Flex align="center" gap={2} py={2}>
                 <GlobalSpinner />
                  <Text fontSize="sm" color={mutedText}>
                    Loading PickDengu points...
                  </Text>
                </Flex>
              ) : pickupPointsError ? (
                <Flex
                  align={{ base: "stretch", sm: "center" }}
                  justify="space-between"
                  direction={{ base: "column", sm: "row" }}
                  gap={3}
                >
                  <Text fontSize="sm" color={mutedText}>
                    {pickupPointsError}
                  </Text>
                  <Button size="sm" onClick={() => void loadPickupPoints()}>
                    Retry
                  </Button>
                </Flex>
              ) : pickupPoints.length === 0 ? (
                <Text fontSize="sm" color={mutedText}>
                  No PickDengu points are currently available.
                </Text>
              ) : (
                <Menu placement="bottom-start" matchWidth>
                  <MenuButton
                    as={Button}
                    w="100%"
                    h="40px"
                    minH="40px"
                    px={3}
                    py={0}
                    bg={inputBg}
                    color={textColor}
                    border={`1px solid ${
                      selectedDropoffPoint ? theme.primary : inputBorder
                    }`}
                    borderRadius={cardsBorderRadii}
                    textAlign="left"
                    fontWeight="500"
                    rightIcon={<ChevronDown size={15} />}
                    _hover={{
                      borderColor: theme.primary,
                      bg: softBg,
                    }}
                    _active={{
                      borderColor: theme.primary,
                      bg: softBg,
                    }}
                  >
                    <Flex align="center" gap={2.5} minW={0}>
                      <Warehouse
                        size={15}
                        color={selectedDropoffPoint ? theme.primary : mutedText}
                      />
                      <Box minW={0} flex="1">
                        <Text
                          fontSize="sm"
                          fontWeight={selectedDropoffPoint ? "700" : "500"}
                          color={selectedDropoffPoint ? textColor : mutedText}
                          noOfLines={1}
                        >
                          {selectedDropoffPoint?.name ||
                            "Select PickDengu drop-off point"}
                          {selectedDropoffPoint?.isDefault ? " · Main" : ""}
                        </Text>
                        {selectedDropoffPoint && (
                          <Text
                            fontSize="10px"
                            lineHeight="12px"
                            color={mutedText}
                            noOfLines={1}
                          >
                            {[
                              selectedDropoffPoint.city,
                              selectedDropoffPoint.address,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </Text>
                        )}
                      </Box>
                    </Flex>
                  </MenuButton>

                  <MenuList
                    bg={inputBg}
                    color={textColor}
                    borderColor={borderColor}
                    borderRadius={cardsBorderRadii}
                    boxShadow="lg"
                    py={0.5}
                    maxH="280px"
                    overflowY="auto"
                    zIndex={30}
                    minW="100%"
                  >
                    {pickupPoints.map((point) => {
                      const selected = selectedDropoffPointId === point.id;

                      return (
                        <MenuItem
                          key={`dropoff-${point.id}`}
                          onClick={() => selectDropoffPoint(point)}
                          bg={selected ? softBg : inputBg}
                          _hover={{ bg: softBg }}
                          _focus={{ bg: softBg }}
                          h="40px"
                          minH="40px"
                          maxH="40px"
                          px={3}
                          py={0}
                          overflow="hidden"
                        >
                          <Flex align="center" gap={2.5} w="100%" h="100%">
                            <Warehouse
                              size={14}
                              color={selected ? theme.primary : mutedText}
                            />
                            <Box minW={0} flex="1">
                              <Flex align="center" gap={2}>
                                <Text
                                  fontSize="12px"
                                  lineHeight="13px"
                                  fontWeight={selected ? "800" : "700"}
                                  color={selected ? theme.primary : textColor}
                                  noOfLines={1}
                                >
                                  {point.name}
                                </Text>
                                {point.isDefault && (
                                  <Text
                                    fontSize="9px"
                                    lineHeight="10px"
                                    fontWeight="800"
                                    color={theme.primary}
                                    textTransform="uppercase"
                                  >
                                    Main
                                  </Text>
                                )}
                              </Flex>
                              <Text
                                fontSize="9px"
                                lineHeight="10px"
                                color={mutedText}
                                mt={0}
                                noOfLines={1}
                              >
                                {[point.city, point.address]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </Text>
                            </Box>
                          </Flex>
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Menu>
              )}
            </Box>
          )}
        </Box>

        <Box>
          <Text mb={2} fontSize="sm" color={mutedText} fontWeight="600">
            Delivery instructions (optional)
          </Text>

          <Textarea
            value={deliveryInstructions}
            onChange={(e) => setDeliveryInstructions(e.target.value)}
            placeholder="For example: Call on arrival, use the side gate, or handle the parcel carefully."
            bg={inputBg}
            color={textColor}
            border={`1px solid ${inputBorder}`}
            _placeholder={{ color: theme.inputPlaceholder }}
            _hover={{ borderColor: borderStrong }}
            _focus={{
              borderColor: focusBorder,
              boxShadow: `0 0 0 1px ${focusBorder}`,
            }}
            minH="96px"
            resize="vertical"
          />
        </Box>

        <Box>
          <Flex
            justify="space-between"
            align="center"
            mb={3}
            flexWrap="wrap"
            gap={2}
          >
            <Heading size="sm" color={textColor}>
              What are you sending?
            </Heading>

            <Button
              size="sm"
              leftIcon={<Plus size={16} />}
              onClick={addItem}
              color={isDark ? theme.text : theme.secondary}
              bg={isDark ? "rgba(255,255,255,0.04)" : "white"}
              border={`1px solid ${borderColor}`}
              _hover={{
                bg: isDark ? "rgba(255,255,255,0.07)" : "rgba(42,44,59,0.03)",
                borderColor: borderStrong,
              }}
              borderRadius={cardsBorderRadii}
            >
              Add Item
            </Button>
          </Flex>

          <VStack spacing={3} align="stretch">
            {items.map((item, index) => (
              <Box
                key={index}
                bg={softBg}
                borderRadius={cardsBorderRadii}
                p={4}
                boxShadow={cardShadow}
              >
                <VStack spacing={3} align="stretch">
                  <SimpleGrid
                    columns={{ base: 1, md: 12 }}
                    spacing={3}
                    alignItems="center"
                  >
                    <Box gridColumn={{ base: "span 1", md: "span 7" }}>
                      <FormControl isRequired isInvalid={itemNameErrors[index]}>
                        <FormLabel htmlFor={`delivery-item-name-${index}`} fontSize="sm">
                          Item {index + 1} name
                        </FormLabel>
                        <Input
                          id={`delivery-item-name-${index}`}
                          ref={(element) => { itemNameRefs.current[index] = element; }}
                          placeholder={`Item ${index + 1} name`}
                          value={item.name}
                          onChange={(e) => updateItemName(index, e.target.value)}
                          onBlur={() => setItems((prev) => prev.map((entry, i) =>
                            i === index ? { ...entry, nameTouched: true } : entry,
                          ))}
                          aria-invalid={itemNameErrors[index]}
                          aria-describedby={itemNameErrors[index] ? `delivery-item-name-error-${index}` : undefined}
                          border="1px solid"
                          borderColor={itemNameErrors[index] ? "red.500" : inputBorder}
                          bg={inputBg}
                          color={textColor}
                          fontWeight="600"
                          _placeholder={{ color: theme.inputPlaceholder }}
                          _hover={{ borderColor: itemNameErrors[index] ? "red.500" : borderStrong }}
                          _focus={{
                            borderColor: focusBorder,
                            boxShadow: `0 0 0 1px ${focusBorder}`,
                          }}
                        />
                        {itemNameErrors[index] && (
                          <FormErrorMessage id={`delivery-item-name-error-${index}`} role="alert">
                            Enter a name for item {index + 1} before continuing.
                          </FormErrorMessage>
                        )}
                      </FormControl>
                    </Box>

                    <Box gridColumn={{ base: "span 1", md: "span 3" }}>
                      <Flex
                        align="center"
                        justify="space-between"
                        border={`1px solid ${inputBorder}`}
                        borderRadius={cardsBorderRadii}
                        bg={inputBg}
                        px={2}
                        minH="40px"
                      >
                        <IconButton
                          aria-label={`Decrease quantity for item ${index + 1}`}
                          icon={<Minus size={16} />}
                          variant="ghost"
                          size="sm"
                          onClick={() => decreaseQty(index)}
                        />
                        <Text fontWeight="700" color={textColor}>
                          {item.qty}
                        </Text>
                        <IconButton
                          aria-label={`Increase quantity for item ${index + 1}`}
                          icon={<Plus size={16} />}
                          variant="ghost"
                          size="sm"
                          onClick={() => increaseQty(index)}
                        />
                      </Flex>
                    </Box>

                    <Box gridColumn={{ base: "span 1", md: "span 2" }}>
                      <Button
                        w="100%"
                        onClick={() => removeItem(index)}
                        color={isDark ? theme.text : theme.secondary}
                        bg={isDark ? "rgba(255,255,255,0.04)" : "white"}
                        border={`1px solid ${borderColor}`}
                        _hover={{
                          bg: isDark
                            ? "rgba(255,255,255,0.07)"
                            : "rgba(42,44,59,0.03)",
                          borderColor: borderStrong,
                        }}
                        borderRadius={cardsBorderRadii}
                        leftIcon={<X size={16} />}
                      >
                        Remove
                      </Button>
                    </Box>
                  </SimpleGrid>

                  <Box>
                    <Text
                      mb={2}
                      fontSize="sm"
                      color={mutedText}
                      fontWeight="600"
                    >
                      Select item size
                    </Text>

                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                      {parcelOptions.map((option) => {
                        const IconComp = option.icon;
                        const selected = item.size === option.key;
                        const accent =
                          option.key === "small"
                            ? brand.electricBlue
                            : option.key === "medium"
                              ? brand.indigo
                              : option.key === "large"
                                ? theme.secondary
                                : brand.violet;

                        return (
                          <Box
                            key={`${index}-${option.key}`}
                            p={3}
                            borderRadius={cardsBorderRadii}
                            cursor="pointer"
                            onClick={() =>
                              updateItemSize(index, option.key as ParcelSize)
                            }
                            transition="all 0.2s ease"
                            bg={
                              selected
                                ? isDark
                                  ? `linear-gradient(135deg, ${accent}22 0%, rgba(255,255,255,0.02) 100%)`
                                  : `linear-gradient(135deg, ${accent}14 0%, rgba(255,255,255,0.98) 100%)`
                                : inputBg
                            }
                            boxShadow={selected ? theme.shadowMd : "none"}
                            outline={
                              selected
                                ? `1px solid ${accent}55`
                                : `1px solid ${inputBorder}`
                            }
                            _hover={{
                              transform: "translateY(-2px)",
                              boxShadow: theme.shadowSm,
                            }}
                          >
                            <VStack spacing={2} align="stretch">
                              <Flex justify="center">
                                <Flex
                                  align="center"
                                  justify="center"
                                  bg={
                                    selected
                                      ? `${accent}22`
                                      : isDark
                                        ? "rgba(255,255,255,0.03)"
                                        : "rgba(42,44,59,0.04)"
                                  }
                                  borderRadius={cardsBorderRadii}
                                  p={2}
                                >
                                  <IconComp
                                    size={20}
                                    color={selected ? accent : mutedText}
                                  />
                                </Flex>
                              </Flex>

                              <Text
                                fontWeight="bold"
                                textAlign="center"
                                color={textColor}
                                fontSize="sm"
                              >
                                {option.label}
                              </Text>

                              <Text
                                fontSize="xs"
                                color={subtleText}
                                textAlign="center"
                              >
                                {option.dimension}
                              </Text>
                            </VStack>
                          </Box>
                        );
                      })}
                    </SimpleGrid>
                  </Box>
                </VStack>
              </Box>
            ))}
          </VStack>
        </Box>

        {fromCoords && toCoords && directions && (
          <Box bg={softBg} borderRadius={cardsBorderRadii} p={4} boxShadow={cardShadow}>
            <VStack align="stretch" spacing={3} mb={3}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <Text color={textColor}>
                  <strong>Distance:</strong> {distance}
                </Text>
                <Text color={textColor}>
                  <strong>Duration:</strong> {duration}
                </Text>
                <Text color={textColor}>
                  <strong>Total Items:</strong> {totalItems}
                </Text>
                <Text color={textColor}>
                  <strong>Different Entries:</strong> {sanitizedItems.length}
                </Text>
              </SimpleGrid>

              <Divider borderColor={borderColor} />

              <VStack align="start" spacing={1}>
                {sanitizedItems.map((item, index) => (
                  <Text
                    key={`${item.name}-${index}`}
                    color={textColor}
                    fontSize="sm"
                  >
                    <strong>{item.name}</strong> × {item.qty} —{" "}
                    {getSizeLabel(item.size)}
                  </Text>
                ))}

                <Divider borderColor={borderColor} my={1} />

                <Text color={textColor}>
                  <strong>Distance Fee:</strong> MWK{" "}
                  {itemSizeBreakdown.totalDistanceFee.toLocaleString(
                    undefined,
                    {
                      maximumFractionDigits: 2,
                    },
                  )}
                </Text>

                <Text color={textColor}>
                  <strong>Size Surcharge:</strong> MWK{" "}
                  {itemSizeBreakdown.totalBaseSurcharge.toLocaleString()}
                </Text>

                {cost !== null && (
                  <Text color={textColor} fontWeight="700" fontSize="lg">
                    <strong>Estimated Fee:</strong> MWK {cost.toLocaleString()}
                  </Text>
                )}
              </VStack>
            </VStack>

            <Box borderRadius={cardsBorderRadii} overflow="hidden">
              <MapDisplay
                height="100%"
                directions={directions}
                center={fromCoords || fallbackCenter}
              />
            </Box>
          </Box>
        )}

        <Flex w="100%" justify="flex-start" gap={3} flexWrap="wrap">
          <Button
            onClick={fetchDirections}
            isDisabled={!fromCoords || !toCoords}
            color={isDark ? theme.text : theme.secondary}
            bg={isDark ? "rgba(255,255,255,0.04)" : "white"}
            border={`1px solid ${borderColor}`}
            _hover={{
              bg: isDark ? "rgba(255,255,255,0.07)" : "rgba(42,44,59,0.03)",
              borderColor: borderStrong,
            }}
            _disabled={{
              opacity: 0.6,
              cursor: "not-allowed",
            }}
            borderRadius="5px"
          >
            Recalculate
          </Button>

          <Button
            isLoading={loading}
            onClick={handleContinue}
            minW="190px"
            color={theme.textOnPrimary}
            bgImage={primaryBtnBg}
            boxShadow={theme.shadowSm}
            _hover={{
              bgImage: primaryBtnHover,
              transform: "translateY(-1px)",
              boxShadow: theme.shadowMd,
            }}
            _active={{ transform: "translateY(0)" }}
            borderRadius="5px"
            transition="all 0.2s ease"
          >
            Continue to Checkout
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
};
